import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { deserializeRocket, serializeRocket, type Rocket } from '@openrocket-web/physics';
import { importOrk, type ImportIssue, type OrkImport } from '@openrocket-web/ork';
import type { Action } from '../state/app';
import type { MotorConfig } from '../state/configs';
import { readBytes, readText } from './readFile';

interface Props {
  readonly dispatch: (a: Action) => void;
}

/** Acciones de archivo que la barra de menús dispara sobre los selectores ocultos. */
export interface FileImportHandle {
  openJson(): void;
  openOrk(): void;
}

export interface ImportReport {
  readonly fileName: string;
  readonly ok: boolean;
  readonly issues: readonly ImportIssue[];
  readonly roughness: number | undefined;
}

/** Aplica un archivo .ork al estado. Devuelve el informe que se muestra al usuario. */
export function applyOrk(fileName: string, imported: OrkImport, dispatch: (a: Action) => void): ImportReport {
  if (imported.rocket === undefined) return { fileName, ok: false, issues: imported.issues, roughness: undefined };
  // Los ids del archivo son largos y opacos: dentro de la app las configuraciones se llaman cfg-1, cfg-2...
  const appId = new Map(imported.motorConfigs.map((c, i) => [c.configId, `cfg-${i + 1}`] as const));
  const motorConfigs: MotorConfig[] = imported.motorConfigs.map((c, i) => ({ id: `cfg-${i + 1}`, ...(c.name === undefined ? {} : { name: c.name }), motors: c.motors }));
  dispatch({
    type: 'set-rocket',
    rocket: imported.rocket,
    motorConfigs,
    ...(imported.activeMotorConfigId === undefined ? {} : { activeConfigId: appId.get(imported.activeMotorConfigId)! }),
    sims: imported.simulations.map((i) => ({
      name: i.name,
      ...(i.configId !== undefined && appId.has(i.configId) ? { configId: appId.get(i.configId)! } : {}),
      launch: i.launch,
      wind: { speed: i.wind.speed, direction: i.wind.direction, turbulenceIntensity: i.wind.turbulenceIntensity },
      ...(imported.roughness === undefined ? {} : { roughness: imported.roughness }),
    })),
  });
  return { fileName, ok: true, issues: imported.issues, roughness: imported.roughness };
}

/** Descarga el diseño como JSON, sin pasar por ningún servidor. */
export function downloadRocket(rocket: Rocket): void {
  const blob = new Blob([serializeRocket(rocket)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${rocket.name.replace(/[^w.-]+/g, '_') || 'cohete'}.rocket.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Selectores de archivo ocultos (los abre el menú Archivo) y el informe de importación, que queda
 * visible aunque el menú se cierre.
 */
export const FileImport = forwardRef<FileImportHandle, Props>(function FileImport({ dispatch }, ref) {
  const orkInput = useRef<HTMLInputElement>(null);
  const jsonInput = useRef<HTMLInputElement>(null);
  const [report, setReport] = useState<ImportReport | undefined>();

  useImperativeHandle(ref, () => ({ openJson: () => jsonInput.current?.click(), openOrk: () => orkInput.current?.click() }));

  async function onOrk(files: FileList | null): Promise<void> {
    const file = files?.[0];
    if (file === undefined) return;
    const imported = importOrk(await readBytes(file));
    setReport(applyOrk(file.name, imported, dispatch));
  }

  async function onJson(files: FileList | null): Promise<void> {
    const file = files?.[0];
    if (file === undefined) return;
    const read = deserializeRocket(await readText(file));
    if (read.rocket === undefined) {
      setReport({ fileName: file.name, ok: false, issues: read.issues.map((i) => ({ severity: i.severity, kind: 'invalid', path: i.path, message: i.message })), roughness: undefined });
      return;
    }
    dispatch({ type: 'set-rocket', rocket: read.rocket });
    setReport({ fileName: file.name, ok: true, issues: read.issues.map((i) => ({ severity: i.severity, kind: 'unsupported-component', path: i.path, message: i.message })), roughness: undefined });
  }

  const problems = report?.issues.filter((i) => i.severity !== 'info') ?? [];
  const infos = report?.issues.filter((i) => i.severity === 'info') ?? [];

  return (
    <>
      <input ref={jsonInput} type="file" hidden aria-label="Importar diseño (.json)" accept=".json,application/json" onChange={(e) => void onJson(e.target.files)} />
      <input ref={orkInput} type="file" hidden aria-label="Importar de OpenRocket (.ork)" accept=".ork,.xml" onChange={(e) => void onOrk(e.target.files)} />

      {report !== undefined && (
        <div className="import-report" role={report.ok ? 'status' : 'alert'}>
          <h2>{report.ok ? `Importado «${report.fileName}»` : `No se pudo importar «${report.fileName}»`}</h2>
          {problems.length === 0 && report.ok && <p>Sin contenido omitido.</p>}
          {problems.length > 0 && (
            <>
              <p>{report.ok ? `Se omitieron o aproximaron ${problems.length} elemento${problems.length === 1 ? '' : 's'}:` : 'Motivo:'}</p>
              <ul className="issue-list issue-list--warning">
                {problems.map((i, n) => (
                  <li key={n}>
                    {i.path !== '' && <code>{i.path}</code>} {i.message}
                  </li>
                ))}
              </ul>
            </>
          )}
          {infos.length > 0 && (
            <details>
              <summary>
                {infos.length} nota{infos.length === 1 ? '' : 's'} informativa{infos.length === 1 ? '' : 's'}
              </summary>
              <ul className="issue-list">
                {infos.map((i, n) => (
                  <li key={n}>
                    {i.path !== '' && <code>{i.path}</code>} {i.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <button type="button" onClick={() => setReport(undefined)}>
            Cerrar informe
          </button>
        </div>
      )}
    </>
  );
});
